package no.vareflow.web

import no.vareflow.app.domain.*
import no.vareflow.app.report.*

/** A month the report can be made from: an open month (live counts) or a closed one (Historikk). */
private class ReportSource(
    val key: String, val label: String, val yearMonth: String, val location: String,
    /** Status text for the report subtitle, in the report's language. */
    val status: (AppLanguage) -> String,
    val lines: () -> List<ReportLine>
)

/** Last choices in the report dialog (kept while the page is open). */
private val reportPrefs = object {
    var grouping = ReportGrouping.CATEGORY
    var layout = ReportLayout.SINGLE
    var format = ReportFormat.XLSX
    var sourceKey: String? = null
    /** Language of the report itself; starts as the app language. */
    var language: AppLanguage? = null
}

private fun WebApp.reportSources(user: UserAccount): List<ReportSource> {
    val seesAll = seesAllCategories(user)
    val productById = core.products.associateBy { it.id }
    val open = openSessions().map { session ->
        ReportSource("open:${session.id}", "${formatYearMonth(session.yearMonth)} · ${session.locationName} (${session.status.label.lowercase()}, live)",
            session.yearMonth, session.locationName, { lang -> session.status.labelIn(lang) }) {
            val assigned = core.assignmentsForSession(session.id).filter { user.id in it.assigneeIds }.map { it.category }.toSet()
            val departments = core.departmentsForSession(session.id)
            core.products.filter { seesAll || it.category in assigned }.mapNotNull { product ->
                val quantities = departments.mapNotNull { core.entry(session.id, product.id, it.id)?.quantity }
                if (quantities.isEmpty()) null
                else ReportLine(product.varenummer, product.name, product.category, product.supplier, product.unit, quantities.sum(), product.pricePerUnit, product.undergruppe)
            }
        }
    }
    val closed = core.sessions
        .filter { seesAll || it.userId == user.id || it.inventorySessionId?.let { id -> core.assignmentsForSession(id).any { a -> user.id in a.assigneeIds } } == true }
        .sortedWith(compareByDescending<CountSession> { it.yearMonth }.thenByDescending { it.createdAt })
        .map { snapshot ->
            ReportSource("closed:${snapshot.id}", "${formatYearMonth(snapshot.yearMonth)} · ${snapshot.locationName} (${t("avsluttet", "closed")})",
                snapshot.yearMonth, snapshot.locationName, { lang -> tr(lang, "Avsluttet", "Closed") + " ${formatDateTime(snapshot.createdAt)}" }) {
                val assigned = snapshot.inventorySessionId?.let { id -> core.assignmentsForSession(id).filter { user.id in it.assigneeIds }.map { it.category }.toSet() }.orEmpty()
                snapshot.lines.groupBy { it.productId.ifBlank { it.productName } }.mapNotNull { (_, group) ->
                    val first = group.first()
                    val product = productById[first.productId]
                    val category = product?.category ?: UNKNOWN_CATEGORY
                    if (!seesAll && category !in assigned) null
                    else ReportLine(product?.varenummer.orEmpty(), first.productName, category, product?.supplier.orEmpty(), first.unit,
                        group.sumOf { it.quantity }, first.pricePerUnit, product?.undergruppe.orEmpty())
                }
            }
        }
    return open + closed
}

fun WebApp.showReportDialog(user: UserAccount) {
    val sources = reportSources(user)
    if (sources.isEmpty()) { toast(t("Ingen tellinger å lage rapport fra ennå.", "No counts to make a report from yet.")); return }
    if (sources.none { it.key == reportPrefs.sourceKey }) {
        reportPrefs.sourceKey = (if (analyseView == "historikk") sources.firstOrNull { it.key.startsWith("closed:") } else null)?.key
            ?: sources.first().key
    }
    val canSeePrice = AccessRules.hasPermission(user, Permission.PRODUCT_VIEW_PRICE)
    openDialog(t("Lag rapport", "Make report")) { close ->
        val reportLanguage = reportPrefs.language ?: I18n.language
        div("field") {
            span("field-label", t("Telling", "Count"))
            selectBox(this@showReportDialog, "rep-source", sources.map { it.key to it.label }, reportPrefs.sourceKey.orEmpty(), t("Telling", "Count")) { reportPrefs.sourceKey = it }
        }
        div("report-option") {
            div("field-label", t("Gruppering", "Grouping"))
            div("chip-row wrap") {
                ReportGrouping.entries.forEach { g -> chip(if (g == ReportGrouping.BOTH) t("Begge", "Both") else g.label, reportPrefs.grouping == g) { reportPrefs.grouping = g; requestRender() } }
            }
        }
        div("report-option") {
            div("field-label", t("Oppsett", "Layout"))
            div("chip-row wrap") {
                ReportLayout.entries.forEach { l -> chip(l.label, reportPrefs.layout == l) { reportPrefs.layout = l; requestRender() } }
            }
            div("field-hint", reportPrefs.layout.hint + if (reportPrefs.format == ReportFormat.PDF && reportPrefs.layout == ReportLayout.PER_GROUP) t(" (i PDF: ny side per gruppe)", " (in PDF: new page per group)") else "")
        }
        div("report-option") {
            div("field-label", "Format")
            div("chip-row wrap") {
                ReportFormat.entries.forEach { f -> chip(f.label, reportPrefs.format == f) { reportPrefs.format = f; requestRender() } }
            }
        }
        div("report-option") {
            div("field-label", t("Språk i rapporten", "Report language"))
            div("chip-row wrap") {
                AppLanguage.entries.forEach { lang -> chip(lang.nativeName, reportLanguage == lang) { reportPrefs.language = lang; requestRender() } }
            }
        }
        if (!canSeePrice) p(t("Rapporten viser ikke priser og verdier (du har ikke tilgang til innkjøpspris).", "The report shows no prices or values (you have no access to purchase prices)."), "muted tiny")
        dialogActions {
            button(t("Avbryt", "Cancel"), "btn text") { close() }
            button(t("Lag rapport", "Make report"), "btn primary") {
                val source = sources.first { it.key == reportPrefs.sourceKey }
                val lang = reportLanguage
                val request = ReportRequest(
                    title = tr(lang, "Varetelling", "Stock count") + " " + formatYearMonth(source.yearMonth, lang),
                    subtitle = "${source.location} · ${source.status(lang)} · " + tr(lang, "laget", "made") + " ${formatDateTime(nowMillis())} " + tr(lang, "av", "by") + " ${user.name}",
                    lines = source.lines(),
                    grouping = reportPrefs.grouping,
                    layout = reportPrefs.layout,
                    showPrices = canSeePrice,
                    undergruppeOrder = core.orderedCategories().flatMap { undergrupperIn(core.products, it) },
                    categoryOrder = core.orderedCategories(),
                    language = lang,
                    companyName = if (cloudMode) cloud.company?.name ?: cloud.membership?.companyName.orEmpty() else "",
                    logoJpeg = if (cloudMode) cloud.logo?.jpeg else null,
                    svinn = core.svinnFor(source.yearMonth)
                )
                val bytes = Reports.create(request, reportPrefs.format)
                download(ReportBuilder.fileName(source.yearMonth, source.location, reportPrefs.format, lang), reportPrefs.format.mimeType, bytes)
                close()
                toast(t("Rapporten er lastet ned (${reportPrefs.format.label}).", "The report has been downloaded (${reportPrefs.format.label})."))
            }
        }
    }
}

/** Saves [bytes] as a file through the browser's normal download. */
private fun download(fileName: String, mimeType: String, bytes: ByteArray) {
    val array: dynamic = bytes
    val u8: dynamic = js("new Uint8Array(array.buffer, array.byteOffset, array.length)")
    val options: dynamic = js("({})")
    options.type = mimeType
    val parts: dynamic = js("[]")
    parts.push(u8)
    val blob: dynamic = js("new Blob(parts, options)")
    val url = window.URL.createObjectURL(blob)
    val a = document.createElement("a")
    a.href = url
    a.download = fileName
    a.style.display = "none"
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.setTimeout({ window.URL.revokeObjectURL(url) }, 2000)
}
